import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { ErrorMessage, Loading, NotFound } from '@Components';
import { useFetchData } from '@CustomHooks';
import { Claim, Options, Permission } from '@Models';
import { bopsApi } from '@Network';
import { RootState } from '@Store';
import ManualUpload from './manualUpload';

interface ManualUploadProps {
  hasPermission: boolean;
}

const ManualUploadContainer = ({ hasPermission }: ManualUploadProps) => {
  const { t } = useTranslation();
  const [selectedRetailer, setRetailer] = useState<Options>();

  const { data: retailers, isLoading: isLoadingRetailers, error: errorRetailers } = useFetchData(bopsApi.getRetailers);

  useEffect(() => {
    if (retailers && retailers.length > 0 && !selectedRetailer) {
      setRetailer(retailers[0]);
    }
  }, [retailers]);

  const {
    data: clients,
    isLoading: isLoadingManualUpload,
    error: errorManualUpload,
    refetch,
  } = useFetchData(bopsApi.getManualUpload, selectedRetailer?.value, !selectedRetailer);

  if (!isLoadingRetailers && errorRetailers) {
    return <ErrorMessage internalMessage="Retailers not found" />;
  }

  if (isLoadingRetailers || (selectedRetailer && !clients && isLoadingManualUpload)) {
    return <Loading title={t('ManualUpload.Loading')} />;
  }

  if (!hasPermission) return <NotFound />;

  if (!retailers || retailers.length === 0) {
    return <ErrorMessage internalMessage="No retailers available." />;
  }

  if (selectedRetailer && errorManualUpload) {
    return (
      <ErrorMessage
        internalMessage={`Unable to get configuration for ${selectedRetailer.label}: ${errorManualUpload}`}
      />
    );
  }

  if (selectedRetailer && !clients && !isLoadingManualUpload && !errorManualUpload) {
    return <ErrorMessage internalMessage={`No manual upload data found for ${selectedRetailer.label}.`} />;
  }

  return (
    <ManualUpload
      clients={clients || []}
      refetch={refetch}
      isFetching={isLoadingManualUpload}
      retailers={retailers}
      selectedRetailer={selectedRetailer}
      setRetailer={setRetailer}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const { authorization } = state;
  const { permissions, claims } = authorization;
  const manualUpload = claims && claims[Claim.ManualUpload];
  const hasPermission = permissions.includes(Permission.ManualUpload);

  return { hasPermission, manualUpload };
};

export default connect(mapStateToProps)(ManualUploadContainer);
